import type { ManualSection } from '../manual.types';

export const cpdSection: ManualSection = {
  slug: 'cpd',
  title: 'CPD — Detección de buzón',
  icon: 'ki-abstract-26',
  summary:
    'Templates, Operators, Routes y Overrides: cómo se combinan, qué significa cada campo y cómo comprobar qué política le va a tocar a una llamada.',
  audience: 'QA, ingeniería de voz y supervisión.',
  topics: [
    {
      id: 'que-es',
      title: 'Qué hace el CPD',
      blocks: [
        {
          kind: 'p',
          text: '**CPD = Call Progress Detection.** El objetivo es detectar que quien "contestó" es un **buzón de voz** y cortar la llamada **antes de que el carrier la facture**. El corte se hace con un **SIP 603 Decline**.'
        },
        {
          kind: 'p',
          text: 'Lo importante para entender la pantalla: **la detección la hace el FreeSWITCH, no este portal**. Raptor guarda la política (qué buscar, con qué umbrales, qué hacer al detectar) y se la entrega a los FS. El backend no está en el camino del audio.'
        },
        {
          kind: 'h4',
          text: 'Detección por tono (early media)'
        },
        {
          kind: 'p',
          text: 'El modo clásico. Mientras la llamada suena, el FS escucha la frecuencia del **beep** característico del buzón de cada carrier. Si la oye las veces configuradas, cuelga con 603.'
        },
        {
          kind: 'code',
          label: 'Forma del patrón que se le pasa al FS',
          text: 'vm<operador> <frecuencia> r +<timeout_ms> hangup 21 <hits>\n\nEjemplo Telcel:  vmtelcel 404 r +1000 hangup 21 1\n\n21    = causa de colgado CALL_REJECTED\nhits  = cuántas veces hay que oír el tono para decidir'
        },
        {
          kind: 'table',
          head: [
            'Carrier',
            'Grupo de ruta',
            'Frecuencia',
            'Repeticiones',
            'Espera antes de escuchar',
            'Nota'
          ],
          align: ['start', 'center', 'center', 'center', 'center', 'start'],
          rows: [
            ['TELCEL', '10', '404 Hz', '1', '0 s', ''],
            [
              'AT&T',
              '20',
              '419 Hz',
              '2',
              '0 s',
              'Se piden 2 porque a veces llega el 200 OK entre el 2.º y el 3.er beep'
            ],
            [
              'MOVISTAR',
              '30',
              '425 Hz',
              '2',
              '25 s',
              'Su tono de llamada usa la misma frecuencia: hay que dejarlo pasar antes de escuchar'
            ],
            [
              'ALTÁN',
              '40',
              '426 Hz',
              '1',
              '30 s',
              'Calibrado con **una sola muestra**; el menos confiable'
            ],
            ['onering', '99', '—', '—', '—', 'Bypass: pasa sin CPD']
          ],
          caption: 'Calibración vigente desde el 30 de junio de 2026 (validada por QA).'
        },
        {
          kind: 'h4',
          text: 'Detección por voz (después de contestar)'
        },
        {
          kind: 'p',
          text: 'Hay buzones que contestan y reproducen un mensaje hablado **sin beep**. Esos el modo por tono no los ve, y hoy pasan como contactadas. Para eso está el modo **Voice / AMD**, que analiza el patrón de voz y silencio ya con la llamada contestada. Ver [el modo Voice](/manual/cpd#modo-voice).'
        }
      ]
    },
    {
      id: 'cadena-de-resolucion',
      title: 'Cómo se decide la política de una llamada (lo más importante)',
      blocks: [
        {
          kind: 'p',
          text: 'Hay cuatro pantallas de CPD porque la configuración se arma **por capas**. Para cada llamada, el backend parte de un valor por omisión y le va encimando lo que encuentre, de lo más general a lo más específico. **Gana siempre lo más específico.**'
        },
        {
          kind: 'code',
          label: 'Orden en que se aplican las capas',
          text: 'DEFAULT  →  Template  →  Route  →  Operator  →  Route+Operator\n(base)                                              (gana)'
        },
        {
          kind: 'kv',
          items: [
            {
              k: 'DEFAULT',
              v: 'Lo que trae el sistema si nadie configuró nada: modo `energy`, acción `hangup_603`, y los umbrales 8000 / −40 / 1500 / 1200.'
            },
            {
              k: 'Template',
              v: 'Una política **completa** con nombre, pensada para reutilizarse. Es la única capa donde todos los campos obligatorios tienen que estar.'
            },
            { k: 'Route', v: 'Ajustes para un grupo de ruta (o sea, para un carrier).' },
            { k: 'Operator', v: 'Ajustes para un operador.' },
            {
              k: 'Route + Operator',
              v: 'La combinación exacta. Es la capa más específica y la que gana sobre todas.'
            }
          ]
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Vacío significa "heredar", no "cero"',
          text: 'En los formularios de Operators, Routes y Overrides, un campo que dejas en blanco **no se manda** y la llamada se queda con lo que traía de la capa anterior. Solo se sobrescribe lo que escribes. Por eso el selector de modo tiene la opción "— Heredar —" y no viene preseleccionado.'
        },
        {
          kind: 'note',
          tone: 'success',
          title: 'No adivines: usa "Ver política efectiva"',
          text: 'En las pantallas de CPD hay un botón que te pregunta operador y grupo de ruta, y te muestra **la configuración final** con la que se va a evaluar esa combinación, más la lista de capas que participaron. Es la forma correcta de comprobar un cambio, y lo primero que hay que hacer antes de reportar que "el CPD no está tomando mi configuración".'
        }
      ]
    },
    {
      id: 'pantallas',
      title: 'Las cuatro pantallas',
      blocks: [
        {
          kind: 'table',
          head: ['Pantalla', 'Para qué sirve', 'Campo que la identifica'],
          rows: [
            [
              '**Templates**',
              'Políticas completas con nombre, para reutilizar. Es la base que las demás capas ajustan.',
              'Nombre'
            ],
            ['**Operators**', 'Configuración por operador.', '`operatorId` (texto, hasta 80)'],
            ['**Routes**', 'Configuración por grupo de ruta / carrier.', '`routeGroupId` (número)'],
            [
              '**Overrides**',
              'La combinación operador + grupo de ruta. La capa más específica.',
              'Los dos juntos'
            ]
          ]
        },
        {
          kind: 'p',
          text: 'Las tres últimas comparten el mismo formulario: datos de identificación, un selector de **template** del que parten, y el bloque de **parámetros forzados (overrides)** donde escribes solo lo que quieres cambiar.'
        },
        {
          kind: 'h4',
          text: 'Acciones disponibles en cada tabla'
        },
        {
          kind: 'kv',
          items: [
            { k: 'Crear / Editar', v: 'Abre el formulario. Requiere rol supervisor o superior.' },
            {
              k: 'Duplicar',
              v: 'Abre el formulario precargado con los valores de ese registro, para crear uno parecido sin volver a capturar.'
            },
            {
              k: 'Habilitar / Deshabilitar',
              v: 'Un registro deshabilitado no participa en la resolución.'
            },
            {
              k: 'Ver política efectiva',
              v: 'La comprobación descrita arriba. **Disponible también para el rol operador.**'
            },
            { k: 'Recargar FS', v: 'Ver [Aplicar cambios](/manual/cpd#recargar-fs).' }
          ]
        }
      ]
    },
    {
      id: 'campos',
      title: 'Qué significa cada campo',
      blocks: [
        {
          kind: 'h4',
          text: 'Modo de detección'
        },
        {
          kind: 'table',
          head: ['Modo', 'Cómo decide', 'Cuándo usarlo'],
          rows: [
            [
              '**Energy**',
              'Mide energía de audio y silencios contra los umbrales.',
              'Es el modo por omisión del sistema.'
            ],
            [
              '**Tone Detect**',
              'Escucha la frecuencia del beep del buzón.',
              'El modo en producción hoy para los cuatro carriers.'
            ],
            ['**DTMF**', 'Espera dígitos del otro lado.', 'Casos especiales.'],
            ['**ML**', 'Clasificador con umbral de confianza.', 'Reservado, aún no en uso.'],
            [
              '**Voice / AMD**',
              'Analiza patrón de voz y silencio **después** de que contestan.',
              'Buzones que hablan y no dan beep. Ver [más abajo](/manual/cpd#modo-voice).'
            ]
          ]
        },
        {
          kind: 'h4',
          text: 'Acción al detectar buzón'
        },
        {
          kind: 'kv',
          items: [
            {
              k: 'Colgar con 603 Decline',
              v: 'Lo normal. Corta la llamada antes de que el carrier facture. Deja `Hangup-Cause = CALL_REJECTED`.'
            },
            {
              k: 'Continuar la llamada',
              v: 'Detecta y registra, pero no cuelga. Útil para calibrar sin afectar tráfico.'
            },
            {
              k: 'Transferir',
              v: 'Manda la llamada a otro destino. **Exige llenar "Transferir a"** (hasta 64 caracteres).'
            }
          ]
        },
        {
          kind: 'h4',
          text: 'Campos del modo por tono'
        },
        {
          kind: 'kv',
          items: [
            {
              k: 'ToneSpec',
              v: 'El patrón literal que se le pasa al FS. Solo acepta letras, números, espacios y `_ , . - + =` (hasta 255). La restricción es de seguridad: el texto se inyecta tal cual en el comando del FS.'
            },
            {
              k: 'Carrier del tono',
              v: 'TELCEL, AT&T, MOVISTAR o ALTÁN. En un **template** puedes poner el carrier en lugar del ToneSpec: basta con uno de los dos.'
            },
            {
              k: 'Delay antes de tone_detect',
              v: 'Milisegundos de espera antes de empezar a escuchar (0 a 300 000). Es lo que evita que el tono de llamada de Movistar se confunda con su beep de buzón.'
            }
          ]
        },
        {
          kind: 'h4',
          text: 'Umbrales (aplican al modo Energy)'
        },
        {
          kind: 'table',
          head: ['Campo', 'Rango', 'Valor por omisión', 'Qué controla'],
          align: ['start', 'center', 'center', 'start'],
          rows: [
            [
              'Max Analysis',
              '500 – 60 000 ms',
              '8 000',
              'Cuánto tiempo máximo analiza antes de rendirse.'
            ],
            ['Min Energy', '−80 – 0 dBFS', '−40', 'Debajo de este nivel se considera silencio.'],
            [
              'Min Voice',
              '100 – 20 000 ms',
              '1 500',
              'Cuánta voz seguida hace falta para considerarla voz.'
            ],
            [
              'Max Silence',
              '100 – 20 000 ms',
              '1 200',
              'Cuánto silencio seguido corta el análisis.'
            ],
            ['DTMF dígitos humano', '0 a 8 caracteres de `0-9 * #`', '—', 'Solo en modo DTMF.'],
            ['ML confidence min', '0 – 1', '—', 'Solo en modo ML. **Es de 0 a 1, no de 0 a 10.**']
          ]
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Los umbrales viajan siempre, aunque no apliquen',
          text: 'Una política completa (un **template**) siempre lleva los cuatro umbrales numéricos, incluso si el modo es `tone` y no los usa. Se guardan tal cual y vuelven a tener efecto si algún día cambias ese template a modo Energy. Por eso el formulario te los pide aunque el modo elegido no los ocupe.'
        },
        {
          kind: 'h4',
          text: 'Bypass'
        },
        {
          kind: 'p',
          text: 'Marcar **Bypass** hace que la llamada pase **sin CPD**. No genera veredicto y queda fuera de los porcentajes de buzón y humano; se contabiliza aparte. Es lo correcto para rutas sin calibrar, como `onering`.'
        }
      ]
    },
    {
      id: 'modo-voice',
      title: 'Modo Voice / AMD y el campo voiceSpec',
      blocks: [
        {
          kind: 'p',
          text: 'El modo **Voice** usa el módulo AMD del FreeSWITCH para analizar el patrón de voz y silencio **una vez contestada** la llamada. Está pensado para los buzones que reproducen "Bienvenido al buzón de voz…" sin dar beep.'
        },
        {
          kind: 'p',
          text: 'El campo **VoiceSpec** son los parámetros de ese análisis, escritos como pares `clave=entero` separados por comas.'
        },
        {
          kind: 'code',
          label: 'Ejemplo de voiceSpec',
          text: 'total_analysis_time=6000,after_greeting_silence=800,greeting=2500'
        },
        {
          kind: 'h4',
          text: 'Las diez claves válidas'
        },
        {
          kind: 'code',
          text: 'initial_silence            greeting\nafter_greeting_silence     total_analysis_time\nmin_word_length            between_words_silence\nmaximum_number_of_words    maximum_word_length\nsilence_threshold          silence_notsure'
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'Una clave mal escrita se rechaza a propósito',
          text: 'El módulo del FS **ignora en silencio** las claves que no conoce. Si escribieras `greetings=2500` (con s), el análisis correría con otros valores y nadie se enteraría. Por eso tanto el formulario como el backend rechazan cualquier clave que no esté en la lista de arriba: más vale un error al guardar que un falso positivo colgándole a un cliente real.'
        },
        {
          kind: 'note',
          tone: 'warn',
          title: 'En un template, el modo Voice exige voiceSpec',
          text: 'Sin él, un nodo que no tenga su archivo de configuración de AMD correría con todos los umbrales en cero. La política tiene que bastarse a sí misma, así que el campo es obligatorio en ese caso. En Operators / Routes / Overrides es opcional, porque ahí puedes estar heredando el del template.'
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'En campañas con agente de IA hay que acotar el análisis',
          text: 'Si el análisis dura demasiado puede dispararse a media conversación. En esos casos conviene bajar `total_analysis_time`.'
        }
      ]
    },
    {
      id: 'escenarios',
      title: 'Los dos momentos de la detección y qué significa cada veredicto',
      blocks: [
        {
          kind: 'p',
          text: 'Una llamada se puede cazar en **dos momentos distintos**. Conviene saber cuál fue, porque no significan lo mismo ni ahorran lo mismo.'
        },
        {
          kind: 'table',
          head: ['Momento', 'Qué escucha', 'Quién decide', 'Qué se ahorra'],
          align: ['start', 'start', 'start', 'start'],
          rows: [
            [
              '**Early media** (antes del 200 OK)',
              'El **beep** del buzón, por frecuencia',
              '`tone_detect` con el patrón del template',
              'Todo: el carrier nunca llega a facturar'
            ],
            [
              '**Post-answer** (después del 200 OK)',
              'El **patrón de voz y silencio** del saludo',
              '`mod_amd` con el `voiceSpec` del template',
              'Los segundos que habría durado el mensaje completo'
            ]
          ]
        },
        {
          kind: 'p',
          text: 'El primero es el CPD de siempre. El segundo existe porque hay buzones que **contestan solos y reproducen una grabación sin beep**: el detector de tono no tiene nada que oír. En la flota son alrededor del **20 % de las llamadas contestadas**.'
        },
        { kind: 'h4', text: 'Cómo decide el AMD' },
        {
          kind: 'p',
          text: 'El AMD no entiende lo que dicen: mide **cuánto habla, cuántas veces y cuánto calla**. Una persona contesta con dos o tres palabras y se queda esperando; un buzón arranca una locución corrida.'
        },
        {
          kind: 'table',
          head: ['Causa', 'Qué la disparó', 'Veredicto'],
          align: ['start', 'start', 'center'],
          rows: [
            [
              'SILENCEAFTERGREETING',
              'Habló poco y se calló. Es el camino normal de una persona',
              'HUMAN'
            ],
            ['LONGGREETING', 'El saludo se pasó de `greeting`', 'MACHINE'],
            [
              'MAXWORDLENGTH',
              'Una sola ráfaga de voz más larga que `maximum_word_length`',
              'MACHINE'
            ],
            ['MAXWORDS', 'Contó más ráfagas que `maximum_number_of_words`', 'MACHINE'],
            ['INITIALSILENCE', 'Contestó y no emitió nada', 'MACHINE'],
            ['TOOLONG', 'Se acabó `total_analysis_time` sin conclusión', 'NOTSURE']
          ]
        },
        {
          kind: 'note',
          tone: 'warn',
          title: 'NOTSURE nunca cuelga',
          text: 'Es una regla de negocio, no una limitación del módulo: preferimos pagar un buzón que colgarle a una persona. Solo `MACHINE` corta la llamada.'
        },
        { kind: 'h4', text: 'El parámetro delicado y el riesgo de falso positivo' },
        {
          kind: 'p',
          text: 'El criterio de éxito del AMD no es cuántos buzones caza, sino **a cuántas personas no les cuelga**. Un falso positivo es cortarle la llamada a alguien que sí contestó, y eso no aparece en ninguna gráfica: se ve como una llamada más clasificada como buzón.'
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'Cuidado con maximum_number_of_words',
          text: 'El valor por defecto del módulo es **3**. En español un saludo normal, "Bueno, quién habla", son tres ráfagas de voz, así que puede salir MACHINE por MAXWORDS. En la prueba del 10 sep un buzón real se cazó justo por esa causa a los **1200 ms**, antes de que `LONGGREETING` alcanzara a dispararse: exactamente el mismo camino que tomaría una persona. Antes de encender el AMD sobre un tráfico nuevo hay que pasar un grupo de control de personas y confirmar que salen HUMAN.'
        },
        { kind: 'h4', text: 'Dónde se configura' },
        {
          kind: 'p',
          text: 'El `voiceSpec` del **template** es la fuente de verdad, igual que el `toneSpec`. Se cambia desde estas pantallas y aplica **en caliente**: los FS releen la política y su caché dura 30 segundos, así que no hay que reiniciar ningún servicio.'
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Un nodo puede traer un respaldo local',
          text: 'Los FS aceptan un spec en su propia configuración, para el caso en que el template todavía no lo traiga. **Gana siempre el del backend.** Si un nodo se comporta distinto al resto, revisar eso es lo primero.'
        },
        { kind: 'h4', text: 'El caso del FS de entrada con agente de IA' },
        {
          kind: 'p',
          text: 'En el nodo que recibe las llamadas de un agente de IA, el AMD corre **antes de conectar al agente**, con la llamada del agente todavía timbrando. Eso tiene tres consecuencias que conviene conocer.'
        },
        {
          kind: 'ul',
          items: [
            'Si sale MACHINE, el agente **nunca se conecta**: no se gasta un solo segundo de conversación.',
            'Si sale HUMAN, la persona escucha alrededor de **1.3 s de silencio** después de contestar, antes de que entre el agente. Es la pausa normal de cualquier llamada.',
            'El análisis es **limpio**: como todavía no hay nada puenteado, no puede llegarle por eco el audio propio. En los nodos del Blaster ese eco sí contamina la ventana y es una causa conocida de MACHINE falso.'
          ]
        },
        {
          kind: 'note',
          tone: 'success',
          title: 'Por eso ese nodo sirve para calibrar',
          text: 'Es el único punto de la flota donde el AMD oye solamente al destino. Los veredictos que salen de ahí son los que valen para ajustar los umbrales del resto.'
        },
        { kind: 'h4', text: 'Cómo se ve en los reportes' },
        {
          kind: 'p',
          text: 'Un buzón cortado en early media y uno cortado post-answer llegan los dos como `verdict = voicemail`. Para distinguirlos, el evento lleva el campo **`CPD-Phase`** con `early_media` o `post_answer`. Hoy el backend todavía no lo guarda, así que en el dashboard se ven mezclados.'
        }
      ]
    },
    {
      id: 'recargar-fs',
      title: 'Aplicar los cambios: el botón Recargar FS',
      blocks: [
        {
          kind: 'p',
          text: 'Guardar un cambio lo deja en la base, pero los FS traen la política **en caché**. El botón **Recargar FS** avisa a todos los nodos para que la vuelvan a leer.'
        },
        {
          kind: 'steps',
          items: [
            'Guarda el operador, la ruta o el override.',
            'Comprueba con **Ver política efectiva** que la combinación quedó como esperabas.',
            'Presiona **Recargar FS**.',
            'La respuesta te dice a cuántos nodos les llegó el aviso. Si dice 0, ningún FS está escuchando y el cambio **no** está en producción.'
          ]
        },
        {
          kind: 'note',
          tone: 'warn',
          title: 'Sin recargar, el cambio no aplica',
          text: 'Es la causa número uno de "configuré algo y no pasó nada". Guardar y recargar son dos pasos distintos, a propósito: así puedes preparar varios cambios y aplicarlos todos juntos.'
        },
        {
          kind: 'p',
          text: 'Recargar requiere rol **supervisor** o superior. El rol operador ve las pantallas en solo lectura pero conserva "Ver política efectiva".'
        }
      ]
    }
  ]
};
